import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useMutation } from 'urql'
import Button from './Button'
import Input from './Input'
import Modal from './Modal'
import { Responses, UserRoles } from '../util/consts'
import SelectInput from './SelectInput'
import { RemoveUser, UpdateUser } from '../graphql/mutations'
import BoxNotification from './BoxNotification'
import ConfirmModal from './ConfirmModal'

const initialState = {
    name: '',
    email: '',
    role: '',
    password: '',
}

const initialErrorsState = {
    name: null,
    email: null,
    role: null,
    password: null,
    generic: null,
}

const userRoles = Object.entries(UserRoles).map(([, value])=> ({
    label: `roles.${value}`,
    value,
}))

const EditUserModal = ({
    show,
    close,
    onSuccess,
    user,
}) => {

    const { t } = useTranslation()
    const [item, setItem] = useState(initialState)
    const [errors, setErrors] = useState(initialErrorsState)
    const [, updateUser] = useMutation(UpdateUser)
    const [, removeUser] = useMutation(RemoveUser)
    const [showConfirmModal, setShowConfirmModal] = useState(false)
    const [loading, setLoading] = useState(false)

    useEffect(() => setItem(initialState), [])

    useEffect(() => {
        if (user) {
            setItem(user)
            return
        }
        setItem(initialState)
    }, [user])

    const hasInputErrors = () => {
        let hasErrors = false
        const errorState = {
            ...initialErrorsState,
        }

        if (item.name.length < 3) {
            hasErrors = true
            errorState.name = t('users.fill_field')
        }

        if (item.email.length < 2) {
            hasErrors = true
            errorState.email = t('users.fill_field')
        }

        if (item.role.length < 3) {
            hasErrors = true
            errorState.role = t('users.fill_field')
        }

        if (item.password.length > 0 && item.password.length < 4) {
            hasErrors = true
            errorState.password = t('users.password_length_required')
        }

        setErrors({
            ...errorState,
        })
        return hasErrors
    }

    const handleUpdate = async () => {
        if (hasInputErrors()) return
        setLoading(true)
        try {
            const { data } = await updateUser({
                id: item.id,
                data: {
                    email: item.email,
                    role: item.role,
                    name: item.name,
                    password: item?.password || undefined,
                }
            })

            if (!data?.updateUser || data.updateUser !== Responses.Success) {
                setErrors({
                    ...initialErrorsState,
                    generic: data.updateUser === Responses.Exists ? t('users.user_exists') : t('users.add_error'),
                })
                return
            }

            if (onSuccess) onSuccess()
        } catch (err) {
            setErrors({
                ...initialErrorsState,
                generic: t('users.add_error'),
            })
            console.log(err)
        } finally {
            setLoading(false)
        }
    }

    const handleRemove = async () => {
        setLoading(true)

        try {
            const { data } = await removeUser({
                id: item.id,
            })

            if (!data?.removeUser || data.removeUser !== Responses.Success) {
                setErrors({
                    ...initialErrorsState,
                    generic: t('users.remove_error'),
                })
                return
            }

            if (onSuccess) onSuccess(data.removeUser)
        } catch (err) {
            setErrors({
                ...initialErrorsState,
                generic: t('users.remove_error'),
            })
            console.log(err)
        } finally {
            setLoading(false)
        }
    }

    const handleClose = () => {
        if (close) close()
        setErrors(initialErrorsState)
        setItem(initialState)
    }

    const setField = (field, value) => {
        setErrors({
            ...errors,
            [field]: null,
        })
        setItem({
            ...item,
            [field]: value,
        })
    }

    return (
        <Modal
            className={'modal-user'}
            show={show}
            close={handleClose}
            title={ t('users.edit_title') }
        >
            <SelectInput
                label={ t('users.role') }
                options={userRoles}
                value={userRoles.find(x => x.value === item.role)}
                getOptionLabel={(option) => t(option.label)}
                onChange={(option) => setField('role', option.value)}
                error={errors.role}
            />
            <Input
                label={ t('users.name') }
                placeholder={ t('users.name') }
                value={item.name}
                onChange={(e) => setField('name', e.target.value)}
                error={errors.name}
            />
            <Input
                label={ t('users.email') }
                value={item.email}
                onChange={(e) => setField('email', e.target.value)}
                error={errors.email}
            />
            <Input
                label={ t('users.password') }
                value={item.password}
                onChange={(e) => setField('password', e.target.value)}
                error={errors.password}
            />

            {
                errors?.generic ?
                <BoxNotification
                    message={errors.generic}
                    type={'error'}
                />
                :
                <></>
            }

            <div className={'modal-actions'}>
                <Button
                    label={ t('users.remove') }
                    className={'btn-cancel'}
                    onClick={() => setShowConfirmModal(true)}
                    loading={loading}
                />

                <Button
                    label={ t('users.save') }
                    onClick={handleUpdate}
                    loading={loading}
                />
            </div>
            <ConfirmModal
                show={showConfirmModal}
                close={() => setShowConfirmModal(false)}
                onConfirm={handleRemove}
                title={ t('users.confirm_delete') }
            />
        </Modal>
    )
}

EditUserModal.propTypes = {
    show: PropTypes.bool,
    close: PropTypes.func,
    onSuccess: PropTypes.func,
    user: PropTypes.object,
}

export default EditUserModal